// Project list signal — shared between Sidebar and Home screen.
// One in-flight reload at a time; mutations push optimistic state then refetch.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type {
  Project,
  ProjectCreate,
  ProjectFromStructure,
  ProjectListItem,
  ProjectUpdate,
  ProjectWithCounts,
} from "@juuplan/shared";
import { api } from "../lib/api";

export interface ProjectsState {
  loading: boolean;
  items: ProjectListItem[];
  error: string | null;
}

const initial: ProjectsState = { loading: true, items: [], error: null };
const projectsSignal: Signal<ProjectsState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadProjects(): Promise<void> {
  projectsSignal.value = { ...projectsSignal.value, loading: true, error: null };
  try {
    const items = await api<ProjectListItem[]>("/api/projects");
    projectsSignal.value = { loading: false, items, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    projectsSignal.value = { loading: false, items: [], error: message };
  } finally {
    inFlight = null;
  }
}

export function useProjects(): ProjectsState {
  useEffect(() => {
    if (!inFlight && projectsSignal.value.loading) {
      inFlight = loadProjects();
    }
  }, []);
  return projectsSignal.value;
}

export function refreshProjects(): Promise<void> {
  inFlight = loadProjects();
  return inFlight;
}

export async function createProject(input: ProjectCreate): Promise<Project> {
  const created = await api<Project>("/api/projects", {
    method: "POST",
    body: input,
  });
  await refreshProjects();
  return created;
}

export async function updateProject(
  id: number,
  patch: ProjectUpdate
): Promise<Project> {
  const updated = await api<Project>(`/api/projects/${id}`, {
    method: "PATCH",
    body: patch,
  });
  await refreshProjects();
  return updated;
}

export async function deleteProject(id: number): Promise<void> {
  await api(`/api/projects/${id}`, { method: "DELETE" });
  await refreshProjects();
}

export async function createProjectFromStructure(body: ProjectFromStructure): Promise<Project> {
  const created = await api<Project>("/api/projects/from-structure", { method: "POST", body });
  await refreshProjects();
  return created;
}

export async function fetchProjectDetail(id: number): Promise<ProjectWithCounts> {
  return api<ProjectWithCounts>(`/api/projects/${id}`);
}
